import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from '../component/Navbar';
import TestAutocomplete from '../component/TestAutocomplete';
import './TestProfileManagement.css';

const API_BASE_URL = 'https://apiv2.labridge.in';

const blankSample = () => ({ sampleType: '', container: '', sampleid: '' });

const blankTest = () => ({
  testId: '',
  testName: '',
  testPrice: '',
  sampleRows: [blankSample()],
  isPackage: false,
  isProfile: false,
  isTest: true,
  mrp: '',
  tat: '',
  report: '',
  tatcalcu: '',
  isVisible: true,
  selectedTest: null,
});

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || error.message || fallback;

const getResults = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

function TestProfileManagement() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('client-wise');
  const [mode, setMode] = useState('single');
  const [singleAction, setSingleAction] = useState('create-and-assign');
  const [tests, setTests] = useState([blankTest()]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [processingCenters, setProcessingCenters] = useState([]);
  const [selectedLabCodes, setSelectedLabCodes] = useState([]);
  const [clientEmail, setClientEmail] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearchError, setClientSearchError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseSummary, setResponseSummary] = useState(null);
  const [formError, setFormError] = useState('');

  const isCreateOnly = mode === 'single' && singleAction === 'create-only';
  const requiresAssignment = !isCreateOnly;

  useEffect(() => {
    const savedLogin = localStorage.getItem('loginData');
    const adminData = savedLogin ? JSON.parse(savedLogin) : null;

    if (!adminData || !Array.isArray(adminData.access) || !adminData.access.includes('9')) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const loadSupportingData = async () => {
      try {
        const [samplesResponse, centersResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/admin/sampledata`),
          axios.get(`${API_BASE_URL}/apiforsubmission/labs`),
        ]);
        setSampleTypes(getResults(samplesResponse.data));
        setProcessingCenters(getResults(centersResponse.data));
      } catch (error) {
        toast.error(getErrorMessage(error, 'Unable to load sample types or processing centers.'));
      }
    };

    loadSupportingData();
  }, []);

  const searchClientByEmail = async () => {
    const email = clientEmail.trim();
    if (!email) {
      setClientSearchError('Enter a client email address to search.');
      return;
    }

    setClientsLoading(true);
    setClientSearchError('');
    setClientOptions([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/labs/${encodeURIComponent(email)}`);
      const results = getResults(response.data);
      if (results.length === 0) {
        setClientSearchError('No client was found for this email address.');
        return;
      }
      setClientOptions(results);
    } catch (error) {
      setClientSearchError(getErrorMessage(error, 'Unable to find a client with this email address.'));
    } finally {
      setClientsLoading(false);
    }
  };

  const updateTest = (index, field, value) => {
    setTests((currentTests) =>
      currentTests.map((test, testIndex) =>
        testIndex === index ? { ...test, [field]: value } : test
      )
    );
  };

  const updateSample = (testIndex, sampleIndex, field, value) => {
    setTests((currentTests) =>
      currentTests.map((test, index) =>
        index === testIndex
          ? {
              ...test,
              sampleRows: test.sampleRows.map((sample, rowIndex) =>
                rowIndex === sampleIndex ? { ...sample, [field]: value } : sample
              ),
            }
          : test
      )
    );
  };

  const selectSample = (testIndex, sampleIndex, sampleId) => {
    const sample = sampleTypes.find((item) => String(item._id || item.sampleid) === sampleId);
    if (!sample) return;

    setTests((currentTests) =>
      currentTests.map((test, index) =>
        index === testIndex
          ? {
              ...test,
              sampleRows: test.sampleRows.map((sampleRow, rowIndex) =>
                rowIndex === sampleIndex
                  ? {
                      ...sampleRow,
                      sampleType: sample.sampleType || '',
                      container: sample.container || '',
                      sampleid: String(sample.sampleid ?? ''),
                    }
                  : sampleRow
              ),
            }
          : test
      )
    );
  };

  const addSampleRow = (testIndex) => {
    setTests((currentTests) =>
      currentTests.map((test, index) =>
        index === testIndex ? { ...test, sampleRows: [...test.sampleRows, blankSample()] } : test
      )
    );
  };

  const removeSampleRow = (testIndex, sampleIndex) => {
    setTests((currentTests) =>
      currentTests.map((test, index) =>
        index === testIndex && test.sampleRows.length > 1
          ? { ...test, sampleRows: test.sampleRows.filter((_, rowIndex) => rowIndex !== sampleIndex) }
          : test
      )
    );
  };

  const splitSampleRows = (test) => {
    const sampleTypes = String(test.sampleType || '').split(',');
    const containers = String(test.container || '').split(',');
    const sampleIds = String(test.sampleid || '').split(',');
    const rowCount = Math.max(sampleTypes.length, containers.length, sampleIds.length);

    if (rowCount === 1 && !sampleTypes[0] && !containers[0] && !sampleIds[0]) return [blankSample()];
    return Array.from({ length: rowCount }, (_, index) => ({
      sampleType: (sampleTypes[index] || '').trim(),
      container: (containers[index] || '').trim(),
      sampleid: (sampleIds[index] || '').trim(),
    }));
  };

  const applySelectedTest = (index, selectedTest) => {
    setTests((currentTests) => currentTests.map((test, testIndex) =>
      testIndex === index
        ? {
            ...test,
            selectedTest,
            testId: String(selectedTest.testId || test.testId),
            testName: selectedTest.testName || test.testName,
            testPrice: selectedTest.testPrice ?? test.testPrice,
            mrp: selectedTest.mrp ?? test.mrp,
            tat: selectedTest.tat ?? test.tat,
            isPackage: String(selectedTest.isPackage) === '1',
            isProfile: String(selectedTest.isProfile) === '1',
            isTest: String(selectedTest.isTest) === '1',
            sampleRows: splitSampleRows(selectedTest),
          }
        : test
    ));
  };

  const addTest = () => setTests((currentTests) => [...currentTests, blankTest()]);
  const removeTest = (index) =>
    setTests((currentTests) => currentTests.filter((_, testIndex) => testIndex !== index));

  const toggleClient = (client) => {
    const clientId = String(client._id || client.id);
    setSelectedClients((currentClients) => {
      const exists = currentClients.some((item) => String(item._id || item.id) === clientId);
      return exists
        ? currentClients.filter((item) => String(item._id || item.id) !== clientId)
        : [...currentClients, client];
    });
  };

  const normalizeTest = (test) => {
    const samples = test.sampleRows.filter((sample) =>
      [sample.sampleType, sample.container, sample.sampleid].some((value) => String(value).trim())
    );
    const { sampleRows, selectedTest, ...testFields } = test;

    return {
    ...testFields,
    testId: String(test.testId).trim(),
    testName: String(test.testName).trim(),
    testPrice: Number(test.testPrice),
    mrp: test.mrp === '' ? '' : test.mrp,
    tat: test.tat.trim(),
    report: test.report.trim(),
    tatcalcu: test.tatcalcu.trim(),
    sampleType: samples.map((sample) => sample.sampleType.trim()).join(','),
    container: samples.map((sample) => sample.container.trim()).join(','),
    sampleid: samples.map((sample) => String(sample.sampleid).trim()).join(','),
    isPackage: test.isPackage ? '1' : '0',
    isProfile: test.isProfile ? '1' : '0',
    isTest: test.isTest ? '1' : '0',
    };
  };

  const validate = () => {
    const errors = [];
    tests.forEach((test, index) => {
      const rowNumber = index + 1;
      if (!String(test.testId).trim()) errors.push(`Row ${rowNumber}: Test ID is required.`);
      if (!String(test.testName).trim()) errors.push(`Row ${rowNumber}: Test name is required.`);
      if (test.testPrice === '' || !Number.isFinite(Number(test.testPrice))) {
        errors.push(`Row ${rowNumber}: Test price must be numeric.`);
      }

      const requiresSampleValues =
        test.sampleRows.length > 1 ||
        test.sampleRows.some((sample) =>
          [sample.sampleType, sample.container, sample.sampleid].some((value) => String(value).trim())
        );
      if (requiresSampleValues) {
        test.sampleRows.forEach((sample, sampleIndex) => {
          const sampleRowNumber = sampleIndex + 1;
          if (!String(sample.sampleType).trim() || !String(sample.container).trim() || !String(sample.sampleid).trim()) {
            errors.push(`Row ${rowNumber}, sample ${sampleRowNumber}: Sample type, container, and sample ID are all required.`);
          }
        });
      }
    });

    if (requiresAssignment && scope === 'client-wise' && selectedClients.length === 0) {
      errors.push('Select at least one client for Client Wise assignment.');
    }
    if ((requiresAssignment && scope === 'processing-center-wise') || isCreateOnly) {
      if (selectedLabCodes.length === 0) {
        errors.push('Select at least one processing center.');
      }
    }

    return errors;
  };

  const submit = async (event) => {
    event.preventDefault();
    const errors = validate();
    if (errors.length > 0) {
      setFormError(errors.join(' '));
      toast.error('Please correct the validation errors.');
      return;
    }

    const normalizedTests = tests.map(normalizeTest);
    let endpoint;
    let payload;

    if (isCreateOnly) {
      endpoint = '/admin/testlist-basics/create';
      payload = {
        labCodes: selectedLabCodes,
        isSpecific: scope === 'client-wise',
        test: normalizedTests[0],
      };
    } else if (mode === 'bulk') {
      endpoint = '/admin/testlist-basics/bulk-create-and-assign';
      payload = { scope, tests: normalizedTests };
    } else {
      endpoint = '/admin/testlist-basics/create-and-assign';
      payload = { scope, test: normalizedTests[0] };
    }

    if (requiresAssignment && scope === 'client-wise') {
      payload.partnerIds = selectedClients.map((client) => String(client._id || client.id));
    }
    if (requiresAssignment && scope === 'processing-center-wise') {
      payload.labCodes = selectedLabCodes;
    }

    setIsSubmitting(true);
    setFormError('');
    setResponseSummary(null);
    try {
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, payload);
      if (response.data?.success === false) {
        throw new Error(response.data?.message || 'The request could not be completed.');
      }

      setResponseSummary(response.data);
      toast.success(response.data?.message || 'Test setup completed successfully.');
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to save the test setup.');
      setFormError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setTests([blankTest()]);
    setSelectedLabCodes([]);
    setSelectedClients([]);
    setClientEmail('');
    setResponseSummary(null);
    setFormError('');
  };

  const scopeHelp = {
    'client-wise': 'Create specific master record using linked lab code and assign only to selected client.',
    'processing-center-wise': 'Create center-level master record and assign to all clients of that center.',
    'all-centers-all-clients': 'Create and assign everywhere.',
  };

  return (
    <div className="wrapper">
      <Navbar />
      <main className="container mt-5 test-profile-page" id="content">
        <div className="mb-4">
          <h1 className="mb-1">Test &amp; Profile Management</h1>
          <p className="text-muted mb-0">Create test master records and safely assign them to clients or processing centers.</p>
        </div>

        <form onSubmit={submit}>
          <section className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="test-scope">Scope</label>
                  <select id="test-scope" className="form-select" value={scope} onChange={(event) => setScope(event.target.value)} disabled={isSubmitting}>
                    <option value="client-wise">Client Wise</option>
                    <option value="processing-center-wise">Processing Center Wise</option>
                    <option value="all-centers-all-clients">All Centers All Clients</option>
                  </select>
                  <div className="form-text">{scopeHelp[scope]}</div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label" htmlFor="test-mode">Mode</label>
                  <select id="test-mode" className="form-select" value={mode} onChange={(event) => setMode(event.target.value)} disabled={isSubmitting}>
                    <option value="single">Single</option>
                    <option value="bulk">Bulk</option>
                  </select>
                </div>
                {mode === 'single' && (
                  <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="single-action">Action</label>
                    <select id="single-action" className="form-select" value={singleAction} onChange={(event) => setSingleAction(event.target.value)} disabled={isSubmitting}>
                      <option value="create-and-assign">Create And Assign</option>
                      <option value="create-only">Create Only</option>
                    </select>
                  </div>
                )}
              </div>

              {scope === 'client-wise' && requiresAssignment && (
                <div className="mt-4">
                  <label className="form-label" htmlFor="client-search">Clients</label>
                  <div className="d-flex gap-2 flex-wrap">
                    <input id="client-search" type="email" className="form-control test-profile-client-search" value={clientEmail} onChange={(event) => setClientEmail(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); searchClientByEmail(); } }} placeholder="Enter client email address" disabled={isSubmitting} />
                    <button type="button" className="btn btn-outline-primary" onClick={searchClientByEmail} disabled={isSubmitting || clientsLoading}>{clientsLoading ? 'Searching…' : 'Find client'}</button>
                  </div>
                  <div className="form-text">Search by the client’s registered email, then select the client from the result.</div>
                  {clientSearchError && <div className="text-danger small mt-1">{clientSearchError}</div>}
                  {clientOptions.length > 0 && (
                    <div className="test-profile-options mt-2" role="listbox" aria-label="Client search results">
                      {clientOptions.map((client) => {
                        const clientId = String(client._id || client.id);
                        const isSelected = selectedClients.some((item) => String(item._id || item.id) === clientId);
                        return <label className="test-profile-option" key={clientId}><input type="checkbox" checked={isSelected} onChange={() => toggleClient(client)} disabled={isSubmitting} /> <span>{client.labName || client.name || clientId}</span></label>;
                      })}
                    </div>
                  )}
                  {selectedClients.length > 0 && <SelectedChips items={selectedClients} getLabel={(client) => client.labName || client.name || client._id} onRemove={toggleClient} disabled={isSubmitting} />}
                </div>
              )}

              {(scope === 'processing-center-wise' || isCreateOnly) && (
                <div className="mt-4">
                  <label className="form-label" htmlFor="processing-centers">{isCreateOnly ? 'Processing centers for master record' : 'Processing centers'}</label>
                  <select id="processing-centers" className="form-select" multiple value={selectedLabCodes} onChange={(event) => setSelectedLabCodes(Array.from(event.target.selectedOptions, (option) => option.value))} disabled={isSubmitting}>
                    {processingCenters.map((center) => <option key={center._id || center.labCode} value={center.labCode}>{center.labName || center.name || center.labCode} ({center.labCode})</option>)}
                  </select>
                  <div className="form-text">Hold Ctrl (Windows) or Command (Mac) to select multiple centers.</div>
                </div>
              )}
            </div>
          </section>

          {formError && <div className="alert alert-danger" role="alert">{formError}</div>}

          <section className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
                <div><h2 className="h4 mb-1">{mode === 'bulk' ? 'Test rows' : 'Test definition'}</h2><p className="text-muted mb-0">Fields marked with * are required.</p></div>
                {mode === 'bulk' && <button type="button" className="btn btn-outline-primary" onClick={addTest} disabled={isSubmitting}>Add row</button>}
              </div>
              {tests.map((test, index) => <TestFields key={index} index={index} test={test} scope={scope} clientId={selectedClients[0]?._id || selectedClients[0]?.id} labCodes={scope === 'processing-center-wise' ? selectedLabCodes : []} sampleTypes={sampleTypes} onChange={updateTest} onTestSelect={applySelectedTest} onSampleChange={selectSample} onSampleFieldChange={updateSample} onAddSample={addSampleRow} onRemoveSample={removeSampleRow} removable={mode === 'bulk' && tests.length > 1} onRemove={removeTest} disabled={isSubmitting} />)}
            </div>
          </section>

          <div className="d-flex gap-2 flex-wrap mb-4">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Saving…' : isCreateOnly ? 'Create master record' : mode === 'bulk' ? 'Create and assign all rows' : 'Create and assign test'}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={reset} disabled={isSubmitting}>Reset form</button>
          </div>
        </form>

        {responseSummary && <ResponseSummary response={responseSummary} />}
      </main>
      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
}

function TestFields({ index, test, scope, clientId, labCodes, sampleTypes, onChange, onTestSelect, onSampleChange, onSampleFieldChange, onAddSample, onRemoveSample, removable, onRemove, disabled }) {
  const setValue = (field) => (event) => onChange(index, field, event.target.value);
  const setChecked = (field) => (event) => onChange(index, field, event.target.checked);

  return (
    <fieldset className="test-profile-row">
      <legend className="visually-hidden">Test row {index + 1}</legend>
      <div className="d-flex justify-content-between align-items-center mb-3"><h3 className="h5 mb-0">{`Test ${index + 1}`}</h3>{removable && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(index)} disabled={disabled}>Remove row</button>}</div>
      <div className="mb-3"><TestAutocomplete scope={scope} clientId={clientId} labCodes={labCodes} onSelect={(selectedTest) => onTestSelect(index, selectedTest)} disabled={disabled} label="Find existing test or profile" /></div>
      <div className="row g-3">
        <Field label="Test ID *"><input className="form-control" value={test.testId} onChange={setValue('testId')} disabled={disabled} /></Field>
        <Field label="Test name *"><input className="form-control" value={test.testName} onChange={setValue('testName')} disabled={disabled} /></Field>
        <Field label="Test price *"><input type="number" min="0" step="0.01" className="form-control" value={test.testPrice} onChange={setValue('testPrice')} disabled={disabled} /></Field>
        <Field label="MRP"><input className="form-control" value={test.mrp} onChange={setValue('mrp')} disabled={disabled} /></Field>
        <Field label="TAT"><input className="form-control" value={test.tat} onChange={setValue('tat')} disabled={disabled} /></Field>
        <Field label="Report"><input className="form-control" value={test.report} onChange={setValue('report')} disabled={disabled} /></Field>
        <Field label="TAT calculation"><input className="form-control" value={test.tatcalcu} onChange={setValue('tatcalcu')} disabled={disabled} /></Field>
      </div>
      <div className="test-profile-samples mt-4">
        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2">
          <div><h4 className="h6 mb-0">Sample details</h4><small className="text-muted">Rows are sent in this exact order as comma-separated values.</small></div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onAddSample(index)} disabled={disabled}>Add sample</button>
        </div>
        {test.sampleRows.map((sampleRow, sampleIndex) => {
          const selectedSample = sampleTypes.find((sample) => sample.sampleType === sampleRow.sampleType);
          return <div className="test-profile-sample-row" key={sampleIndex}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-4"><label className="form-label">Sample type</label><select className="form-select" value={String(selectedSample?._id || selectedSample?.sampleid || '')} onChange={(event) => onSampleChange(index, sampleIndex, event.target.value)} disabled={disabled}><option value="">Select sample type</option>{sampleTypes.map((sample) => <option key={sample._id || sample.sampleid} value={sample._id || sample.sampleid}>{sample.sampleType}</option>)}</select></div>
              <div className="col-12 col-md-3"><label className="form-label">Container</label><input className="form-control" value={sampleRow.container} onChange={(event) => onSampleFieldChange(index, sampleIndex, 'container', event.target.value)} disabled={disabled} /></div>
              <div className="col-10 col-md-3"><label className="form-label">Sample ID</label><input className="form-control" value={sampleRow.sampleid} onChange={(event) => onSampleFieldChange(index, sampleIndex, 'sampleid', event.target.value)} disabled={disabled} /></div>
              <div className="col-2"><button type="button" className="btn btn-outline-danger w-100" onClick={() => onRemoveSample(index, sampleIndex)} disabled={disabled || test.sampleRows.length === 1} aria-label={`Remove sample ${sampleIndex + 1}`}>×</button></div>
            </div>
          </div>;
        })}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-3">
        <Check label="Package" checked={test.isPackage} onChange={setChecked('isPackage')} disabled={disabled} />
        <Check label="Profile" checked={test.isProfile} onChange={setChecked('isProfile')} disabled={disabled} />
        <Check label="Test" checked={test.isTest} onChange={setChecked('isTest')} disabled={disabled} />
        <Check label="Visible" checked={test.isVisible} onChange={setChecked('isVisible')} disabled={disabled} />
      </div>
    </fieldset>
  );
}

function Field({ label, children }) { return <div className="col-12 col-md-6 col-lg-4"><label className="form-label">{label}</label>{children}</div>; }
function Check({ label, checked, onChange, disabled }) { return <label className="form-check-label"><input type="checkbox" className="form-check-input me-2" checked={checked} onChange={onChange} disabled={disabled} />{label}</label>; }

function SelectedChips({ items, getLabel, onRemove, disabled }) {
  return <div className="test-profile-chips mt-2">{items.map((item) => <span className="badge text-bg-primary test-profile-chip" key={item._id || item.id}>{getLabel(item)}<button type="button" onClick={() => onRemove(item)} disabled={disabled} aria-label={`Remove ${getLabel(item)}`}>×</button></span>)}</div>;
}

function ResponseSummary({ response }) {
  const summaryFields = [
    ['Inserted basic records', response.insertedBasicCount ?? response.insertedCount],
    ['Assigned records', response.assignedCount],
    ['Skipped existing basic records', response.skippedExistingBasicCount ?? response.skippedExistingCount],
    ['Skipped existing assignments', response.skippedExistingAssignmentCount],
    ['Skipped lab mismatches', response.skippedLabMismatchCount],
  ];
  const detailGroups = [
    ['Missing partner IDs', response.missingPartnerIds],
    ['Missing partner-lab mappings', response.missingPartnerLabMappings],
    ['Processing center issues', response.requestedProcessingCenterIssues],
    ...Object.entries(response.details || {}).map(([key, value]) => [key.replace(/([A-Z])/g, ' $1'), value]),
  ].filter(([, value]) => Array.isArray(value) ? value.length > 0 : value && Object.keys(value).length > 0);

  return <section className="card mb-4" aria-labelledby="response-summary-heading"><div className="card-body"><h2 id="response-summary-heading" className="h4">Response summary</h2><p className="text-success">{response.message || 'Request completed.'}</p><div className="row g-3">{summaryFields.map(([label, value]) => <div className="col-6 col-md-4" key={label}><div className="test-profile-summary-label">{label}</div><strong>{value ?? 0}</strong></div>)}</div>{detailGroups.length > 0 && <div className="mt-4"><h3 className="h5">Skipped and issue details</h3>{detailGroups.map(([label, value]) => <details className="test-profile-details" key={label}><summary>{label} ({Array.isArray(value) ? value.length : 'view'})</summary><pre>{JSON.stringify(value, null, 2)}</pre></details>)}</div>}</div></section>;
}

export default TestProfileManagement;
